import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Download, LogOut, Building2, FileText, CheckCircle, 
  ArrowLeft, LayoutGrid, Monitor, Zap, Shield, Folder, FolderOutput, Settings, Play
} from "lucide-react";
import { BrandedFooter } from "@/components/BrandedFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_docuflow-68/artifacts/y7jajers_Well%20Connected%20Logo_Feb2024_600px.jpg";

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageUsage, setPageUsage] = useState(null);
  
  // Agent configuration state
  const [agentConfig, setAgentConfig] = useState({
    input_folder: "",
    output_folder: "",
    failed_folder: "",
    configured: false,
    license_key: ""
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [downloadingAgent, setDownloadingAgent] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchUserAccess();
    fetchPageUsage();
    fetchAgentConfig();
  }, []);

  const fetchUserAccess = async () => {
    try {
      const response = await axios.get(`${API}/user/app-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Find the docuflow-extraction access
      const docAccess = response.data.find(a => 
        a.app_slug === "docuflow-extraction" || a.app_slug === "document-processing"
      );
      if (docAccess) {
        setCompanyInfo({ 
          company_id: docAccess.company_id,
          name: docAccess.company_name || "Your Company"
        });
        fetchTemplates(docAccess.company_id);
      }
    } catch (error) {
      console.error("Failed to fetch access:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPageUsage = async () => {
    try {
      const response = await axios.get(`${API}/user/page-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPageUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch page usage:", error);
    }
  };

  const fetchAgentConfig = async () => {
    try {
      const response = await axios.get(`${API}/user/agent-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.configured && response.data.config) {
        setAgentConfig({
          input_folder: response.data.config.input_folder || "",
          output_folder: response.data.config.output_folder || "",
          failed_folder: response.data.config.failed_folder || "",
          configured: true,
          license_key: response.data.config.license_key || ""
        });
        // Load the saved template selection
        if (response.data.config.template_id) {
          setSelectedTemplateId(response.data.config.template_id);
        }
      } else {
        setAgentConfig(prev => ({
          ...prev,
          license_key: response.data.license_key || "",
          configured: false
        }));
      }
    } catch (error) {
      console.error("Failed to fetch agent config:", error);
    }
  };

  const saveAgentConfig = async () => {
    if (!agentConfig.input_folder || !agentConfig.output_folder) {
      toast.error("Please enter both input and output folder paths");
      return;
    }
    
    setSavingConfig(true);
    try {
      await axios.post(`${API}/user/agent-config`, {
        input_folder: agentConfig.input_folder,
        output_folder: agentConfig.output_folder,
        failed_folder: agentConfig.failed_folder || agentConfig.output_folder,
        template_id: selectedTemplateId  // CRITICAL: Include the selected template!
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAgentConfig(prev => ({ ...prev, configured: true }));
      toast.success("Configuration saved successfully!");
    } catch (error) {
      toast.error("Failed to save configuration");
      console.error(error);
    } finally {
      setSavingConfig(false);
    }
  };

  const downloadAgent = async () => {
    if (!agentConfig.configured) {
      toast.error("Please save your configuration first");
      return;
    }
    
    setDownloadingAgent(true);
    try {
      const response = await axios.get(`${API}/user/download-agent`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'docuflow-agent.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Agent downloaded! Extract and run start_agent.bat");
    } catch (error) {
      toast.error("Failed to download agent");
      console.error(error);
    } finally {
      setDownloadingAgent(false);
    }
  };

  const fetchTemplates = async (companyId) => {
    try {
      const response = await axios.get(`${API}/company/${companyId}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      if (response.data.length > 0) {
        setSelectedTemplateId(response.data[0].template_id);
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend to clear server-side session and cookies
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.log("Logout API error (non-critical):", error);
    }
    // Clear local storage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const selectedTemplate = templates.find(t => t.template_id === selectedTemplateId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E01626]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="WELL Connected" className="h-10" />
          <div className="flex items-center gap-3">
            {companyInfo && (
              <Badge variant="secondary" className="text-sm">
                <Building2 className="h-3 w-3 mr-1" />
                {companyInfo.name}
              </Badge>
            )}
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle className="h-3 w-3 mr-1" />Licensed
            </Badge>
            <Button variant="outline" size="sm" onClick={() => navigate("/apps")}>
              <LayoutGrid className="h-4 w-4 mr-2" />All Apps
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-6 flex items-center gap-2 text-sm">
          <button 
            onClick={() => navigate("/apps")} 
            className="text-muted-foreground hover:text-[#E01626] flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Apps
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="font-medium text-[#E01626]">Document Processing</span>
        </div>

        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-[#E01626]">DocuFlow Extraction</h1>
          <p className="text-muted-foreground mt-1">
            Professional document management and organization solution
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Setup Desktop Agent Card */}
          <Card className="border-2 border-slate-200">
            <CardHeader className="bg-gradient-to-r from-[#E01626] to-[#c01020] text-white rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Settings className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-white">Desktop Agent Setup</CardTitle>
                  <CardDescription className="text-white/80">
                    Configure your document processing folders
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* License Key Display */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">License Key Activated</span>
                </div>
                <code className="text-sm text-green-700 font-mono">
                  {agentConfig.license_key ? `${agentConfig.license_key.slice(0,4)}••••${agentConfig.license_key.slice(-4)}` : "Loading..."}
                </code>
              </div>

              {/* Folder Configuration */}
              <div className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Folder className="h-4 w-4 text-[#E01626]" />
                    Input Folder Path
                  </Label>
                  <Input 
                    placeholder="C:\Documents\ToProcess"
                    value={agentConfig.input_folder}
                    onChange={(e) => setAgentConfig(prev => ({ ...prev, input_folder: e.target.value, configured: false }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Place your PDF/image files here for processing
                  </p>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <FolderOutput className="h-4 w-4 text-[#E01626]" />
                    Output Folder Path
                  </Label>
                  <Input 
                    placeholder="C:\Documents\Processed"
                    value={agentConfig.output_folder}
                    onChange={(e) => setAgentConfig(prev => ({ ...prev, output_folder: e.target.value, configured: false }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Successfully processed files will be moved here with new names
                  </p>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Folder className="h-4 w-4 text-amber-500" />
                    Failed Folder Path (Optional)
                  </Label>
                  <Input 
                    placeholder="C:\Documents\Failed (defaults to output folder)"
                    value={agentConfig.failed_folder}
                    onChange={(e) => setAgentConfig(prev => ({ ...prev, failed_folder: e.target.value, configured: false }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Files that couldn't be processed will be moved here
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <Button 
                  onClick={saveAgentConfig}
                  disabled={savingConfig || !agentConfig.input_folder || !agentConfig.output_folder}
                  className="flex-1 bg-[#E01626] hover:bg-[#c01020]"
                >
                  {savingConfig ? (
                    <>Saving...</>
                  ) : agentConfig.configured ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Configuration Saved
                    </>
                  ) : (
                    <>
                      <Settings className="h-4 w-4 mr-2" />
                      Save Configuration
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={downloadAgent}
                  disabled={downloadingAgent || !agentConfig.configured}
                  variant={agentConfig.configured ? "default" : "outline"}
                  className={agentConfig.configured ? "flex-1 bg-green-600 hover:bg-green-700" : "flex-1"}
                >
                  {downloadingAgent ? (
                    <>Preparing Download...</>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Download Agent
                    </>
                  )}
                </Button>
              </div>

              {/* Instructions - Simple and Clean */}
              {agentConfig.configured && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                  <p className="text-sm text-green-800">
                    <strong>✓ Ready!</strong> Download → Extract → Run <code className="bg-green-100 px-1 rounded font-mono">INSTALL.bat</code>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Page Usage Card */}
          {pageUsage && !pageUsage.error && (
            <Card className="border-2 border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#E01626]/10 rounded-lg">
                      <FileText className="h-6 w-6 text-[#E01626]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Your Page Quota</h3>
                      <p className="text-sm text-muted-foreground">Monthly allocation for {pageUsage.company_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold">
                      <span className={pageUsage.pages_used >= pageUsage.page_limit ? "text-red-600" : "text-[#E01626]"}>
                        {pageUsage.pages_used}
                      </span>
                      <span className="text-muted-foreground text-lg font-normal"> / {pageUsage.page_limit}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">pages used</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="w-full bg-slate-100 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all ${
                        pageUsage.pages_used >= pageUsage.page_limit 
                          ? 'bg-red-500' 
                          : pageUsage.pages_used >= pageUsage.page_limit * 0.8 
                            ? 'bg-yellow-500' 
                            : 'bg-[#E01626]'
                      }`}
                      style={{ width: `${Math.min((pageUsage.pages_used / pageUsage.page_limit) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {Math.round((pageUsage.pages_used / pageUsage.page_limit) * 100)}% of quota used
                    </span>
                    <span className={pageUsage.pages_remaining > 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                      {pageUsage.pages_remaining} pages remaining
                    </span>
                  </div>
                </div>
                
                {pageUsage.pages_used >= pageUsage.page_limit * 0.9 && (
                  <div className={`mt-4 p-3 rounded-lg ${
                    pageUsage.pages_used >= pageUsage.page_limit 
                      ? 'bg-red-50 border border-red-200 text-red-800' 
                      : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                  }`}>
                    <p className="text-sm font-medium">
                      {pageUsage.pages_used >= pageUsage.page_limit 
                        ? "⚠️ Quota exceeded! Contact your administrator to increase your page limit."
                        : "⚠️ You're approaching your page limit. Consider contacting your administrator."}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Document Template - Full Width */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#E01626]" />
                Document Template
              </CardTitle>
              <CardDescription>
                Your configured naming convention for processed documents
              </CardDescription>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-amber-800">
                  No templates configured. Contact your administrator.
                </div>
              ) : (
                <>
                  <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                    <SelectTrigger className="w-full md:w-[300px]">
                      <SelectValue placeholder="Select template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.template_id} value={t.template_id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedTemplate && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Filename Format:</p>
                      <code className="text-sm bg-white px-4 py-3 rounded border block font-mono break-all overflow-x-auto max-w-full">
                        {selectedTemplate.filename_format}
                      </code>
                      {selectedTemplate.fields?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm text-muted-foreground mb-2">Extracted Fields:</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedTemplate.fields.map((f, i) => (
                              <Badge key={i} variant="secondary" className="text-sm px-3 py-1">
                                {f.display_name || f.field_name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* License Key Info - Simplified */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">License Active</span>
            </div>
          </CardContent>
        </Card>
      </main>
      <BrandedFooter />
    </div>
  );
}
